import type { CgOrder, CgStatus } from './types';

/**
 * Which status changes the interface will offer.
 *
 * The CG status dropdown has always allowed anything to become anything, with
 * no confirmation. That was survivable while status was only a label. It is
 * not survivable now that a supplier's confirmation moves it: dragging an
 * order back to "PO Sent to ES" after a factory has accepted it and started
 * the sample clock leaves a running countdown attached to an order that
 * claims not to be in production yet.
 *
 * IMPORTANT, and worth repeating wherever this is read: this is a GUARD, not
 * an invariant. It runs in the browser. The database still permits any status
 * to become any other, because cg_orders has one broad policy and no
 * transition trigger. Somebody with the API and a mind to do it can still
 * write whatever they like. What this buys is the accident — the mis-click on
 * a dropdown next to the row you meant — which is the failure that actually
 * happens.
 */

/** From here on, an order is being worked on rather than arranged. */
/**
 * From here on, an order is being worked on rather than arranged.
 *
 * Moved from 'In Production' to 'Sampling' when the sample QA stages were
 * added (20261018000100). 'Sampling' is now the moment a factory accepts and
 * starts making pre-production samples — the same commitment by another
 * company that the threshold always meant. Leaving it at 'In Production'
 * would have quietly allowed an accepted order to be dragged back to
 * 'PO Sent to ES' while a sample clock was already running against it.
 */
export const LOCKED_FROM = 'Sampling';

function sortOrderOf(statusId: string, statuses: CgStatus[]): number | null {
  const found = statuses.find((status) => status.id === statusId);
  return found === undefined ? null : found.sort_order;
}

/**
 * Whether this order has passed the point where going backwards is a mistake.
 *
 * Two independent triggers, either sufficient. The supplier's confirmation is
 * the meaningful one — it is a commitment by another company, and a clock
 * started against it. The status threshold catches an order moved forward by
 * hand without a supplier ever touching the portal, which is how most of them
 * will move for a while yet.
 */
export function isForwardOnly(order: CgOrder, statuses: CgStatus[]): boolean {
  if (order.supplier_confirmed_at !== null) {
    return true;
  }
  const current = sortOrderOf(order.status, statuses);
  const threshold = sortOrderOf(LOCKED_FROM, statuses);
  if (current === null || threshold === null) {
    // An unknown status is not a licence to lock the row: leaving it editable
    // is recoverable, locking it by accident is not.
    return false;
  }
  return current >= threshold;
}

/**
 * Status ids the dropdown should refuse, for this order, right now.
 *
 * Empty until the order is forward-only. After that, everything earlier than
 * where it already is. The current status itself is never blocked — a select
 * whose own value is disabled renders as empty in some browsers, which would
 * look like data loss.
 */
export function blockedStatuses(order: CgOrder, statuses: CgStatus[]): Set<string> {
  if (!isForwardOnly(order, statuses)) {
    return new Set();
  }
  const current = sortOrderOf(order.status, statuses);
  if (current === null) {
    return new Set();
  }
  return new Set(
    statuses
      .filter((status) => status.sort_order < current && status.id !== order.status)
      .map((status) => status.id),
  );
}

/** Why the earlier options are greyed, in one line, or '' when they are not. */
export function forwardOnlyReason(order: CgOrder, statuses: CgStatus[]): string {
  if (!isForwardOnly(order, statuses)) {
    return '';
  }
  return order.supplier_confirmed_at !== null
    ? 'The supplier has accepted this order and the sample clock is running, so it cannot move backwards.'
    : 'This order is in production, so it cannot move backwards.';
}
